import React, { useRef, useEffect, useState } from 'react';
import {
    Users, Car, ShieldAlert, MapPin, Briefcase, User,
    FileText, UploadCloud, X, Film, Plus, Trash2
} from 'lucide-react';

interface ExistingAttachment { id: number; name: string; url?: string; isImage: boolean; }

interface Props {
    formValues: any;
    setFormValues: (data: any) => void;
    files: File[];
    setFiles: (files: File[]) => void;
    // Allegati già caricati sul mandato (in modifica): mostrati per revisione/rimozione.
    existingAttachments?: ExistingAttachment[];
    onRemoveExisting?: (id: number) => void;
}

const inputClass = "w-full p-2.5 bg-gray-50 border border-gray-300 rounded-lg text-sm text-gray-900 focus:bg-white focus:ring-2 focus:ring-orange-400 focus:border-orange-400 outline-none transition-all shadow-sm placeholder-gray-400";

const defaultSoggetto = {
    tipo: 'Privato', nome: '', nato_a: '', data_nascita: '', codice_fiscale: '', piva: '',
    sede_via: '', indirizzo: '', citta: '', sede_citta: '', cap: '', sede_cap: '', prov: '', sede_prov: '',
    telefono: '', email: '', social: '', domicilio_altrove: '', luoghi_frequentati: '', altre_info_casa: '',
    auto: '', auto_conoscenti: '', moto: '', lavoro: '', hobby: '', abitudini: '', fisico: '',
    gia_pedinato: false, attento: false, lavoro_particolare: false, pericoloso: false, armi: false, allegati_nomi: ''
};

export const Step4_SoggettoInvestigato: React.FC<Props> = ({ formValues, setFormValues, files, setFiles, existingAttachments = [], onRemoveExisting }) => {
    const fileInputRef = useRef<HTMLInputElement>(null);
    const [activeTab, setActiveTab] = useState(0);
    const [previewImage, setPreviewImage] = useState<{ url: string; name: string } | null>(null);

    // Pulisce l'object URL quando il modale si chiude o cambia immagine, per evitare leak.
    useEffect(() => {
        if (!previewImage) return;
        return () => URL.revokeObjectURL(previewImage.url);
    }, [previewImage]);

    const openImagePreview = (file: File) => {
        if (!file.type.startsWith('image/')) return;
        setPreviewImage({ url: URL.createObjectURL(file), name: file.name });
    };

    // Inizializzazione, compatibile anche con i mandati vecchi a soggetto singolo
    useEffect(() => {
        if (!formValues.soggetti || formValues.soggetti.length === 0) {
            const hasLegacy = !!formValues.soggetto_nome;
            const initialSoggetto = hasLegacy ? {
                tipo: formValues.soggetto_tipo || 'Privato',
                nome: formValues.soggetto_nome || '',
                nato_a: formValues.soggetto_nato_a || '',
                data_nascita: formValues.soggetto_data_nascita || '',
                codice_fiscale: formValues.soggetto_codice_fiscale || '',
                piva: formValues.soggetto_piva || '',
                sede_via: formValues.soggetto_sede_via || '',
                indirizzo: formValues.soggetto_indirizzo || '',
                citta: formValues.soggetto_citta || '',
                sede_citta: formValues.soggetto_sede_citta || '',
                cap: formValues.soggetto_cap || '',
                sede_cap: formValues.soggetto_sede_cap || '',
                prov: formValues.soggetto_prov || '',
                sede_prov: formValues.soggetto_sede_prov || '',
                telefono: formValues.soggetto_telefono || '',
                email: formValues.soggetto_email || '',
                social: formValues.soggetto_social || '',
                domicilio_altrove: formValues.soggetto_domicilio_altrove || '',
                luoghi_frequentati: formValues.soggetto_luoghi_frequentati || '',
                altre_info_casa: formValues.soggetto_altre_info_casa || '',
                auto: formValues.soggetto_auto || '',
                auto_conoscenti: formValues.soggetto_auto_conoscenti || '',
                moto: formValues.soggetto_moto || '',
                lavoro: formValues.soggetto_lavoro || '',
                hobby: formValues.soggetto_hobby || '',
                abitudini: formValues.soggetto_abitudini || '',
                fisico: formValues.soggetto_fisico || '',
                gia_pedinato: formValues.soggetto_gia_pedinato || false,
                attento: formValues.soggetto_attento || false,
                lavoro_particolare: formValues.soggetto_lavoro_particolare || false,
                pericoloso: formValues.soggetto_pericoloso || false,
                armi: formValues.soggetto_armi || false,
                allegati_nomi: formValues.soggetto_allegati_nomi || ''
            } : { ...defaultSoggetto };

            setFormValues((prev: any) => ({ ...prev, soggetti: [initialSoggetto] }));
        }
    }, []);

    const soggetti = formValues.soggetti || [{ ...defaultSoggetto }];
    const currSoggetto = soggetti[activeTab] || defaultSoggetto;
    const isAzienda = currSoggetto.tipo === 'Azienda';

    const updateSoggetto = (field: string, value: any) => {
        const newSoggetti = [...soggetti];
        newSoggetti[activeTab] = { ...newSoggetti[activeTab], [field]: value };
        setFormValues({ ...formValues, soggetti: newSoggetti });
    };

    // Nessun limite al numero di soggetti per mandato.
    const addSoggetto = () => {
        setFormValues({ ...formValues, soggetti: [...soggetti, { ...defaultSoggetto }] });
        setActiveTab(soggetti.length);
    };

    const removeSoggetto = (index: number) => {
        const newSoggetti = soggetti.filter((_: any, i: number) => i !== index);
        setFormValues({ ...formValues, soggetti: newSoggetti });
        if (activeTab >= newSoggetti.length) setActiveTab(Math.max(0, newSoggetti.length - 1));
    };

    // Gestione File
    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.files && e.target.files.length > 0) {
            setFiles([...files, ...Array.from(e.target.files)]);
        }
    };
    const removeFile = (index: number) => setFiles(files.filter((_, i) => i !== index));
    const formatFileSize = (bytes: number) => {
        if (bytes === 0) return '0 Bytes';
        const k = 1024, sizes = ['Bytes', 'KB', 'MB', 'GB'], i = Math.floor(Math.log(bytes) / Math.log(k));
        return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
    };

    return (
        <div className="animate-in fade-in slide-in-from-right-8 space-y-6 pb-24">
            <div className="bg-white rounded-xl shadow-md border border-gray-200 p-8 border-t-4 border-t-orange-500">

                <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-6 gap-4 border-b border-gray-100 pb-5">
                    <div>
                        <h2 className="text-2xl font-black text-gray-900 flex items-center gap-2">
                            <Users size={28} className="text-orange-600"/> Dati Soggetti Investigati
                        </h2>
                        <p className="text-sm text-gray-500 mt-1">Gestisci uno o più soggetti di indagine all'interno dello stesso mandato.</p>
                    </div>
                </div>

                {/* Tab dei soggetti */}
                <div className="flex flex-wrap gap-2 mb-8 bg-gray-50 p-2 rounded-xl border border-gray-200">
                    {soggetti.map((_: any, idx: number) => (
                        <div key={idx} className={`flex items-center rounded-lg transition-all ${activeTab === idx ? 'bg-orange-500 text-white shadow-md' : 'bg-white text-gray-600 hover:bg-gray-100 border border-gray-200'}`}>
                            <button onClick={() => setActiveTab(idx)} className="px-4 py-2 font-bold text-sm flex-1">
                                Soggetto {idx + 1}
                            </button>
                            {soggetti.length > 1 && (
                                <button onClick={() => removeSoggetto(idx)} className={`p-2 rounded-r-lg transition-colors ${activeTab === idx ? 'hover:bg-orange-600' : 'hover:text-red-600 hover:bg-red-50'}`} title="Rimuovi">
                                    <Trash2 size={16}/>
                                </button>
                            )}
                        </div>
                    ))}
                    <button onClick={addSoggetto} className="px-4 py-2 rounded-lg font-bold text-sm flex items-center gap-2 bg-white border border-dashed border-orange-300 text-orange-600 hover:bg-orange-50 transition-all">
                        <Plus size={16} /> Aggiungi Soggetto
                    </button>
                </div>

                {/* Soggetto attivo */}
                <div className="animate-in fade-in zoom-in-95 duration-200">
                    <div className="flex justify-end mb-4">
                        <div className="flex bg-gray-100 p-1 rounded-lg shrink-0 shadow-inner">
                            <button onClick={() => updateSoggetto('tipo', 'Privato')} className={`px-5 py-2 text-sm font-bold rounded-md transition-all ${!isAzienda ? 'bg-white shadow-sm text-orange-600 ring-1 ring-black/5' : 'text-gray-500 hover:text-gray-700'}`}>Persona Fisica</button>
                            <button onClick={() => updateSoggetto('tipo', 'Azienda')} className={`px-5 py-2 text-sm font-bold rounded-md transition-all ${isAzienda ? 'bg-white shadow-sm text-purple-600 ring-1 ring-black/5' : 'text-gray-500 hover:text-gray-700'}`}>Azienda / Ente</button>
                        </div>
                    </div>

                    <Section icon={<User size={18} className="text-orange-600"/>} title="Anagrafica & Residenza">
                        <div className="col-span-full">
                            <FormLabel required>{isAzienda ? 'Denominazione Azienda' : 'Nome e Cognome'}</FormLabel>
                            <input type="text" className={inputClass} placeholder={isAzienda ? 'Es. Rossi S.r.l.' : 'Es. Mario Rossi'} value={currSoggetto.nome || ''} onChange={e => updateSoggetto('nome', e.target.value)} />
                        </div>
                        {!isAzienda && (<>
                            <div><FormLabel>Nato/a a</FormLabel><input type="text" className={inputClass} value={currSoggetto.nato_a || ''} onChange={e => updateSoggetto('nato_a', e.target.value)} /></div>
                            <div><FormLabel>Data di Nascita</FormLabel><input type="date" className={inputClass} value={currSoggetto.data_nascita || ''} onChange={e => updateSoggetto('data_nascita', e.target.value)} /></div>
                            <div><FormLabel>Codice Fiscale</FormLabel><input type="text" className={`${inputClass} uppercase`} value={currSoggetto.codice_fiscale || ''} onChange={e => updateSoggetto('codice_fiscale', e.target.value)} /></div>
                        </>)}
                        {isAzienda && (
                            <div className="col-span-full md:col-span-1"><FormLabel>P.IVA / Codice Fiscale</FormLabel><input type="text" className={`${inputClass} uppercase`} value={currSoggetto.piva || currSoggetto.codice_fiscale || ''} onChange={e => updateSoggetto('piva', e.target.value)} /></div>
                        )}
                        <div className={isAzienda ? 'col-span-full md:col-span-3' : 'col-span-full'}>
                            <FormLabel>{isAzienda ? 'Sede Legale – Via' : 'Residente / Sede in Via'}</FormLabel>
                            <div className="relative">
                                <MapPin size={16} className="absolute left-3 top-3 text-gray-400"/>
                                <input type="text" className={`${inputClass} pl-10`} value={isAzienda ? (currSoggetto.sede_via || '') : (currSoggetto.indirizzo || '')} onChange={e => updateSoggetto(isAzienda ? 'sede_via' : 'indirizzo', e.target.value)} />
                            </div>
                        </div>
                        <div><FormLabel>Comune</FormLabel><input type="text" className={inputClass} value={isAzienda ? (currSoggetto.sede_citta || '') : (currSoggetto.citta || '')} onChange={e => updateSoggetto(isAzienda ? 'sede_citta' : 'citta', e.target.value)} /></div>
                        <div><FormLabel>CAP</FormLabel><input type="text" maxLength={5} className={inputClass} value={isAzienda ? (currSoggetto.sede_cap || '') : (currSoggetto.cap || '')} onChange={e => updateSoggetto(isAzienda ? 'sede_cap' : 'cap', e.target.value)} /></div>
                        <div><FormLabel>Prov.</FormLabel><input type="text" maxLength={2} className={`${inputClass} uppercase`} value={isAzienda ? (currSoggetto.sede_prov || '') : (currSoggetto.prov || '')} onChange={e => updateSoggetto(isAzienda ? 'sede_prov' : 'prov', e.target.value)} /></div>
                    </Section>

                    <Section icon={<Briefcase size={18} className="text-orange-600"/>} title="Recapiti & Contatti">
                        <div><FormLabel>Telefono</FormLabel><input type="text" className={inputClass} value={currSoggetto.telefono || ''} onChange={e => updateSoggetto('telefono', e.target.value)} /></div>
                        <div><FormLabel>Email</FormLabel><input type="email" className={inputClass} value={currSoggetto.email || ''} onChange={e => updateSoggetto('email', e.target.value)} /></div>
                        <div className="col-span-full"><FormLabel>Social Network</FormLabel><input type="text" className={inputClass} value={currSoggetto.social || ''} onChange={e => updateSoggetto('social', e.target.value)} /></div>
                        <div className="col-span-full"><FormLabel>Domicilio (se diverso dalla residenza)</FormLabel><textarea className={`${inputClass} min-h-[80px] resize-y`} value={currSoggetto.domicilio_altrove || ''} onChange={e => updateSoggetto('domicilio_altrove', e.target.value)} /></div>
                        <div className="col-span-full"><FormLabel>Luoghi Frequentati Abitualmente</FormLabel><textarea className={`${inputClass} min-h-[80px] resize-y`} value={currSoggetto.luoghi_frequentati || ''} onChange={e => updateSoggetto('luoghi_frequentati', e.target.value)} /></div>
                        <div className="col-span-full"><FormLabel>Info Abitazione e Nucleo Familiare</FormLabel><textarea className={`${inputClass} min-h-[80px] resize-y`} value={currSoggetto.altre_info_casa || ''} onChange={e => updateSoggetto('altre_info_casa', e.target.value)} /></div>
                    </Section>

                    <Section icon={<Car size={18} className="text-orange-600"/>} title="Profilo Operativo & Abitudini">
                        <div className="col-span-full"><FormLabel>Veicoli di proprietà</FormLabel><textarea className={`${inputClass} min-h-[80px] resize-y`} value={currSoggetto.auto || ''} onChange={e => updateSoggetto('auto', e.target.value)} /></div>
                        <div className="col-span-full"><FormLabel>Veicoli di famigliari / conoscenti stretti</FormLabel><textarea className={`${inputClass} min-h-[80px] resize-y`} value={currSoggetto.auto_conoscenti || ''} onChange={e => updateSoggetto('auto_conoscenti', e.target.value)} /></div>
                        <div className="col-span-full"><FormLabel>Motocicli</FormLabel><textarea className={`${inputClass} min-h-[80px] resize-y`} value={currSoggetto.moto || ''} onChange={e => updateSoggetto('moto', e.target.value)} /></div>
                        <div className="col-span-full"><FormLabel>Attività lavorativa</FormLabel><textarea className={`${inputClass} min-h-[80px] resize-y`} value={currSoggetto.lavoro || ''} onChange={e => updateSoggetto('lavoro', e.target.value)} /></div>
                        <div className="col-span-full"><FormLabel>Hobby e Sport</FormLabel><textarea className={`${inputClass} min-h-[80px] resize-y`} value={currSoggetto.hobby || ''} onChange={e => updateSoggetto('hobby', e.target.value)} /></div>
                        <div className="col-span-full"><FormLabel>Abitudini / Orari tipici</FormLabel><textarea className={`${inputClass} min-h-[80px] resize-y`} value={currSoggetto.abitudini || ''} onChange={e => updateSoggetto('abitudini', e.target.value)} /></div>
                        <div className="col-span-full"><FormLabel>Caratteristiche fisiche</FormLabel><textarea className={`${inputClass} min-h-[80px] resize-y`} value={currSoggetto.fisico || ''} onChange={e => updateSoggetto('fisico', e.target.value)} /></div>
                    </Section>

                    <div className="mb-10 bg-red-50 p-6 rounded-xl border border-red-200 shadow-inner">
                        <h3 className="text-base font-bold text-red-800 uppercase tracking-wider mb-5 flex items-center gap-2"><ShieldAlert size={20}/> Valutazione Rischi</h3>
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                            <RiskCheck label="Già pedinato in passato?" checked={currSoggetto.gia_pedinato || false} onChange={v => updateSoggetto('gia_pedinato', v)} />
                            <RiskCheck label="Atteggiamento sospettoso?" checked={currSoggetto.attento || false} onChange={v => updateSoggetto('attento', v)} />
                            <RiskCheck label="Attività a rischio?" checked={currSoggetto.lavoro_particolare || false} onChange={v => updateSoggetto('lavoro_particolare', v)} />
                            <RiskCheck label="Soggetto pericoloso?" checked={currSoggetto.pericoloso || false} onChange={v => updateSoggetto('pericoloso', v)} color="red" />
                            <RiskCheck label="Possiede armi?" checked={currSoggetto.armi || false} onChange={v => updateSoggetto('armi', v)} color="red" />
                        </div>
                    </div>

                    <div className="mb-10">
                        <FormLabel>Note sugli allegati (Specifichi per questo soggetto)</FormLabel>
                        <input type="text" className={inputClass} placeholder="Es. Foto consegnate su chiavetta USB..." value={currSoggetto.allegati_nomi || ''} onChange={e => updateSoggetto('allegati_nomi', e.target.value)} />
                    </div>
                </div>

                {/* Upload media comune a tutto il mandato */}
                <div className="pt-2 border-t border-gray-200 mt-8">
                    <h3 className="text-base font-bold text-gray-800 uppercase tracking-wider mb-4 flex items-center gap-2 pb-3 mt-8">
                        <UploadCloud size={20} className="text-blue-500"/> Materiale Allegato Globale (Foto, Video, Documenti)
                    </h3>

                    <div className="border-2 border-dashed border-blue-200 bg-blue-50/30 rounded-2xl p-10 text-center hover:bg-blue-50 transition-colors cursor-pointer group" onClick={() => fileInputRef.current?.click()}>
                        <input type="file" multiple className="hidden" ref={fileInputRef} onChange={handleFileChange} accept="image/*,video/*,application/pdf" />
                        <div className="flex flex-col items-center gap-3 text-gray-500 group-hover:text-blue-700 transition-colors">
                            <div className="p-4 bg-white shadow-sm text-blue-600 rounded-full mb-1 group-hover:scale-110 transition-transform"><UploadCloud size={32}/></div>
                            <p className="font-bold text-gray-800 text-lg">Clicca qui o trascina i file</p>
                        </div>
                    </div>

                    {/* Allegati GIÀ caricati sul mandato (modifica): rivedibili e rimovibili. */}
                    {existingAttachments.length > 0 && (
                        <div className="mt-5">
                            <p className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-2">Allegati già caricati</p>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                {existingAttachments.map(att => (
                                    <div key={att.id} className="flex items-center justify-between p-3.5 bg-white border border-gray-200 shadow-sm rounded-xl hover:border-blue-400 transition-all group/item">
                                        <div className="flex items-center gap-4 overflow-hidden flex-1">
                                            <div className="w-12 h-12 rounded-lg bg-gray-100 flex items-center justify-center text-gray-600 shrink-0 overflow-hidden border border-gray-200">
                                                {att.isImage && att.url
                                                    ? <img src={att.url} alt={att.name} className="w-full h-full object-cover" />
                                                    : <FileText size={24} className="text-gray-400" />}
                                            </div>
                                            <div className="min-w-0">
                                                <p className="text-sm font-bold text-gray-900 truncate" title={att.name}>{att.name}</p>
                                                <p className="text-xs text-gray-500">Già sul mandato</p>
                                            </div>
                                        </div>
                                        <button onClick={() => onRemoveExisting && onRemoveExisting(att.id)} className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-full transition-colors opacity-80 group-hover/item:opacity-100" title="Rimuovi allegato"><Trash2 size={20}/></button>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    {files.length > 0 && (
                        <div className="mt-5">
                          {existingAttachments.length > 0 && <p className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-2">Nuovi allegati</p>}
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 animate-in fade-in">
                            {files.map((file, index) => {
                                const isImage = file.type.startsWith('image/');
                                return (
                                    <div key={index} className="flex items-center justify-between p-3.5 bg-white border border-gray-200 shadow-sm rounded-xl hover:border-blue-400 transition-all group/item">
                                        <div
                                            className={`flex items-center gap-4 overflow-hidden flex-1 ${isImage ? 'cursor-pointer' : ''}`}
                                            onClick={isImage ? () => openImagePreview(file) : undefined}
                                            title={isImage ? 'Clicca per ingrandire' : undefined}
                                        >
                                            <div className="w-12 h-12 rounded-lg bg-gray-100 flex items-center justify-center text-gray-600 shrink-0 overflow-hidden border border-gray-200">
                                                <FileThumbnail file={file} />
                                            </div>
                                            <div className="min-w-0">
                                                <p className="text-sm font-bold text-gray-900 truncate" title={file.name}>{file.name}</p>
                                                <p className="text-xs text-gray-500">{formatFileSize(file.size)}</p>
                                            </div>
                                        </div>
                                        <button onClick={() => removeFile(index)} className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-full transition-colors opacity-80 group-hover/item:opacity-100"><X size={20}/></button>
                                    </div>
                                );
                            })}
                          </div>
                        </div>
                    )}
                </div>

            </div>

            {/* Modale anteprima immagine — chiusura su click backdrop o X. */}
            {previewImage && (
                <div
                    className="fixed inset-0 z-[100] flex items-center justify-center bg-black/80 backdrop-blur-sm p-4"
                    onClick={() => setPreviewImage(null)}
                >
                    <div
                        className="relative max-w-5xl w-full max-h-[90vh] flex flex-col items-center justify-center"
                        onClick={e => e.stopPropagation()}
                    >
                        <button
                            onClick={() => setPreviewImage(null)}
                            className="absolute -top-12 right-0 text-white hover:text-gray-300 bg-black/50 hover:bg-black/70 p-2 rounded-full transition-colors"
                            title="Chiudi"
                        >
                            <X size={24} />
                        </button>
                        <img
                            src={previewImage.url}
                            alt={previewImage.name}
                            className="max-w-full max-h-[85vh] object-contain rounded-lg shadow-2xl"
                        />
                        <p className="absolute -bottom-10 text-white text-sm font-medium truncate max-w-full">{previewImage.name}</p>
                    </div>
                </div>
            )}
        </div>
    );
};

const FileThumbnail: React.FC<{ file: File }> = ({ file }) => {
    const [previewUrl, setPreviewUrl] = useState<string | null>(null);
    useEffect(() => {
        if (file.type.startsWith('image/')) {
            const url = URL.createObjectURL(file);
            setPreviewUrl(url);
            return () => URL.revokeObjectURL(url);
        }
    }, [file]);
    if (previewUrl) return <img src={previewUrl} alt="Anteprima" className="w-full h-full object-cover" />;
    if (file.type.startsWith('video/')) return <Film size={24} className="text-gray-400" />;
    return <FileText size={24} className="text-gray-400" />;
};

const Section: React.FC<{ icon: React.ReactNode; title: string; children: React.ReactNode }> = ({ icon, title, children }) => (
    <div className="mb-10 bg-gray-50/50 p-6 rounded-2xl border border-gray-100">
        <h3 className="text-sm font-bold text-gray-800 uppercase tracking-wider mb-5 flex items-center gap-2 border-b border-gray-200 pb-3">
            <span className="p-1.5 bg-white shadow-sm rounded-md border border-gray-100">{icon}</span>{title}
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">{children}</div>
    </div>
);

const FormLabel: React.FC<{ children: React.ReactNode; required?: boolean }> = ({ children, required }) => (
    <label className="block text-xs font-bold text-gray-700 uppercase tracking-wider mb-1.5">{children} {required && <span className="text-red-500 text-sm">*</span>}</label>
);

const RiskCheck: React.FC<{ label: string; checked: boolean; onChange: (v: boolean) => void; color?: 'red' | 'orange'; bold?: boolean }> = ({ label, checked, onChange, color = 'orange', bold }) => {
    const yesClasses = color === 'red' ? 'bg-white shadow-sm text-red-600 ring-1 ring-red-200' : 'bg-white shadow-sm text-orange-600 ring-1 ring-orange-200';
    return (
        <div className="flex flex-col gap-3 p-4 rounded-xl border border-gray-200 bg-white shadow-sm h-full">
            <span className={`text-sm leading-tight ${bold ? 'font-bold text-gray-900' : 'font-medium text-gray-700'}`}>{label}</span>
            <div className="flex bg-gray-100 p-1 rounded-lg mt-auto shrink-0 shadow-inner">
                <button type="button" onClick={() => onChange(true)} className={`flex-1 py-1.5 text-xs font-bold rounded-md transition-all ${checked ? yesClasses : 'text-gray-500 hover:text-gray-700'}`}>Sì</button>
                <button type="button" onClick={() => onChange(false)} className={`flex-1 py-1.5 text-xs font-bold rounded-md transition-all ${!checked ? 'bg-white shadow-sm text-green-600 ring-1 ring-green-200' : 'text-gray-500 hover:text-gray-700'}`}>No</button>
            </div>
        </div>
    );
};